import { useEffect, useState, useMemo } from "react";
import { 
  getAllInvoices, 
  getInvoicePDF, 
  updateInvoiceStatus,
  getBulkInvoicePDF,
  getBulkInvoiceZip,
  getInvoiceBatches,
  getBatchInvoicePDF,
  getSelectedInvoicesPDF,
  getSelectedInvoicesZip
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Download, 
  Printer, 
  Search,
  RefreshCw,
  FileCheck,
  CheckCircle,
  Clock,
  XCircle,
  FileArchive,
  FileText,
  Calendar,
  Layers,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Ban,
  Settings,
  Save,
  RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { api, getSettings, updateSettings } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const DATE_RANGES = {};

export default function InvoiceHistory() {
  const [invoices, setInvoices] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [invoiceDates, setInvoiceDates] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    loadInvoices();
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      const response = await getInvoiceBatches();
      setBatches(response.data);
    } catch (error) {
      // Batches might not exist yet
      console.log("No batches found");
    }
  };

  // Extract unique invoice dates and compute filtered list
  useEffect(() => {
    const dates = [...new Set(invoices.map(inv => {
      const d = new Date(inv.created_at || inv.invoice_date);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }))].sort((a, b) => new Date(b) - new Date(a));
    setInvoiceDates(dates);
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];

    // Date tab filter
    if (dateFilter !== "all") {
      filtered = filtered.filter(inv => {
        const d = new Date(inv.created_at || inv.invoice_date);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) === dateFilter;
      });
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(inv =>
        inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.buyer_store?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.invoice_date);
      const dateB = new Date(b.invoice_date);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [invoices, searchTerm, dateFilter, sortOrder]);

  const loadInvoices = async () => {
    try {
      const response = await getAllInvoices();
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await getInvoicePDF(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      a.click();
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };

  const handlePrint = async (invoice) => {
    try {
      const response = await getInvoicePDF(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      await updateInvoiceStatus(invoice.id, 'printed');
      await loadInvoices();
    } catch (error) {
      toast.error("Failed to print invoice");
    }
  };

  const handleCancelInvoice = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await api.post(`/invoice/${cancelTarget.id}/cancel`);
      toast.success(res.data.message);
      setCancelTarget(null);
      await loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel invoice");
    } finally {
      setCancelling(false);
    }
  };

  const handleBulkPDF = async () => {
    setDownloading(true);
    try {
      const response = await getBulkInvoicePDF();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'all_invoices.pdf';
      a.click();
      toast.success("Bulk PDF downloaded");
    } catch (error) {
      toast.error("Failed to download bulk PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkZip = async () => {
    setDownloading(true);
    try {
      const response = await getBulkInvoiceZip();
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invoices.zip';
      a.click();
      toast.success("ZIP file downloaded");
    } catch (error) {
      toast.error("Failed to download ZIP");
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkPrint = async () => {
    try {
      const response = await getBulkInvoicePDF();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      toast.success("Bulk print initiated");
    } catch (error) {
      toast.error("Failed to print");
    }
  };

  const handleBatchPrint = async (batchId) => {
    setDownloading(true);
    try {
      const response = await getBatchInvoicePDF(batchId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      toast.success("Batch print initiated");
    } catch (error) {
      toast.error("Failed to print batch");
    } finally {
      setDownloading(false);
    }
  };

  const handleBatchDownload = async (batchId) => {
    setDownloading(true);
    try {
      const response = await getBatchInvoicePDF(batchId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `batch_${batchId.slice(0, 8)}_invoices.pdf`;
      a.click();
      toast.success("Batch PDF downloaded");
    } catch (error) {
      toast.error("Failed to download batch");
    } finally {
      setDownloading(false);
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedInvoices.length === filteredInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map(inv => inv.id));
    }
  };

  const toggleSelectInvoice = (invoiceId) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    }
  };

  const handleSelectedPDF = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("No invoices selected");
      return;
    }
    setDownloading(true);
    try {
      const response = await getSelectedInvoicesPDF(selectedInvoices);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `selected_${selectedInvoices.length}_invoices.pdf`;
      a.click();
      toast.success(`Downloaded ${selectedInvoices.length} invoices as PDF`);
    } catch (error) {
      toast.error("Failed to download selected invoices");
    } finally {
      setDownloading(false);
    }
  };

  const handleSelectedZip = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("No invoices selected");
      return;
    }
    setDownloading(true);
    try {
      const response = await getSelectedInvoicesZip(selectedInvoices);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `selected_${selectedInvoices.length}_invoices.zip`;
      a.click();
      toast.success(`Downloaded ${selectedInvoices.length} invoices as ZIP`);
    } catch (error) {
      toast.error("Failed to download selected invoices");
    } finally {
      setDownloading(false);
    }
  };

  const handleSelectedPrint = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("No invoices selected");
      return;
    }
    setDownloading(true);
    try {
      const response = await getSelectedInvoicesPDF(selectedInvoices);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      toast.success(`Printing ${selectedInvoices.length} invoices`);
    } catch (error) {
      toast.error("Failed to print selected invoices");
    } finally {
      setDownloading(false);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'generated':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Generated</Badge>;
      case 'printed':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Printed</Badge>;
      case 'closed':
        return <Badge variant="outline"><XCircle className="w-3 h-3 mr-1" />Closed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700"><Ban className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const totalValue = filteredInvoices.reduce((sum, inv) => sum + (inv.grand_total || 0), 0);
  const pendingInvoices = filteredInvoices.filter(i => i.status === 'generated' || i.status === 'printed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="invoice-history">
      {/* Settings Toggle */}
      <div className="flex items-center justify-between">
        <div />
        <Button size="sm" variant="outline" onClick={() => setShowSettings(!showSettings)} data-testid="oblik-invoice-settings-btn">
          <Settings className="w-4 h-4 mr-1" />Invoice Settings
        </Button>
      </div>
      {showSettings && <OBLIKInvoiceSettings />}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileCheck className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{filteredInvoices.length}</p>
                <p className="text-xs text-slate-500">Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{pendingInvoices.length}</p>
                <p className="text-xs text-slate-500">Pending Print</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold font-mono">
                  {filteredInvoices.filter(i => i.status === 'printed' || i.status === 'closed').length}
                </p>
                <p className="text-xs text-slate-500">Printed/Closed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div>
              <p className="text-2xl font-bold font-mono">Rs. {totalValue.toLocaleString()}</p>
              <p className="text-xs text-slate-500">Total Value</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      <Card className="bg-slate-900 text-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-lg">Bulk Actions</h3>
              <p className="text-slate-400 text-sm">
                {selectedInvoices.length > 0 
                  ? `${selectedInvoices.length} invoice(s) selected` 
                  : 'Download or print all invoices at once'}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {selectedInvoices.length > 0 ? (
                <>
                  <Button 
                    variant="secondary" 
                    onClick={handleSelectedPrint}
                    disabled={downloading}
                    data-testid="print-selected-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />Print Selected ({selectedInvoices.length})
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={handleSelectedPDF}
                    disabled={downloading}
                    data-testid="pdf-selected-btn"
                  >
                    {downloading ? <div className="spinner w-4 h-4 mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                    PDF ({selectedInvoices.length})
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleSelectedZip}
                    disabled={downloading}
                    className="text-white border-white hover:bg-white/10"
                    data-testid="zip-selected-btn"
                  >
                    <FileArchive className="w-4 h-4 mr-2" />ZIP ({selectedInvoices.length})
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setSelectedInvoices([])}
                    className="text-slate-400 hover:text-white"
                  >
                    Clear Selection
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="secondary" 
                    onClick={handleBulkPrint}
                    disabled={downloading || pendingInvoices.length === 0}
                  >
                    <Printer className="w-4 h-4 mr-2" />Print All ({pendingInvoices.length})
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={handleBulkPDF}
                    disabled={downloading || invoices.length === 0}
                  >
                    {downloading ? <div className="spinner w-4 h-4 mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                    Single PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleBulkZip}
                    disabled={downloading || invoices.length === 0}
                    className="text-white border-white hover:bg-white/10"
                  >
                    <FileArchive className="w-4 h-4 mr-2" />ZIP
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Date Tabs */}
      {invoiceDates.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="invoice-date-tabs">
          <Calendar className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <Tabs value={dateFilter} onValueChange={setDateFilter}>
            <TabsList className="h-9">
              <TabsTrigger value="all" className="text-xs px-3">
                All ({invoices.length})
              </TabsTrigger>
              {invoiceDates.slice(0, 10).map(date => (
                <TabsTrigger key={date} value={date} className="text-xs px-3">
                  {date} ({invoices.filter(inv => {
                    const d = new Date(inv.created_at || inv.invoice_date);
                    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) === date;
                  }).length})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search invoice, PO, or store..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="invoice-search"
              />
            </div>
            
            {/* Sort button */}
            <Button 
              variant="outline" 
              onClick={toggleSortOrder}
              className="flex items-center gap-2"
              data-testid="sort-date-btn"
            >
              {sortOrder === 'desc' ? (
                <><ArrowDown className="w-4 h-4" />Newest First</>
              ) : (
                <><ArrowUp className="w-4 h-4" />Oldest First</>
              )}
            </Button>
            
            <Button variant="outline" onClick={loadInvoices}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for All Invoices and Batches */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">
            <FileCheck className="w-4 h-4 mr-2" />All Invoices ({filteredInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="batches">
            <Layers className="w-4 h-4 mr-2" />Bulk Batches ({batches.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {/* Invoices table */}
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-sm font-medium">
                Invoice History ({filteredInvoices.length})
              </CardTitle>
            </CardHeader>
            <ScrollArea className="h-[450px]">
              {filteredInvoices.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <FileCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg">No invoices found</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10">
                        <Checkbox 
                          checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase">Invoice #</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase">PO #</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase">Store</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase cursor-pointer" onClick={toggleSortOrder}>
                        <div className="flex items-center gap-1">
                          Date
                          {sortOrder === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                        </div>
                      </TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Items</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Amount</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Status</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => (
                      <TableRow 
                        key={invoice.id}
                        className={selectedInvoices.includes(invoice.id) ? 'bg-blue-50' : ''}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={selectedInvoices.includes(invoice.id)}
                            onCheckedChange={() => toggleSelectInvoice(invoice.id)}
                            data-testid={`select-invoice-${invoice.invoice_number}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono font-medium">{invoice.invoice_number}</TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">{invoice.po_number}</TableCell>
                        <TableCell className="text-sm">{invoice.buyer_store || '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{invoice.invoice_date}</TableCell>
                        <TableCell className="text-center font-mono">{invoice.total_pcs}</TableCell>
                        <TableCell className="text-right font-mono font-medium">Rs. {invoice.grand_total?.toLocaleString()}</TableCell>
                        <TableCell className="text-center">{getStatusBadge(invoice.status)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleDownload(invoice)} title="Download" data-testid={`download-${invoice.invoice_number}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handlePrint(invoice)} title="Print" data-testid={`print-${invoice.invoice_number}`}>
                              <Printer className="w-4 h-4" />
                            </Button>
                            {invoice.status !== 'cancelled' && !invoice.voided && (
                              <Button variant="ghost" size="sm" onClick={() => setCancelTarget(invoice)} title="Cancel Invoice"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`cancel-${invoice.invoice_number}`}>
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            {!invoice.voided && (
                              <Button variant="ghost" size="sm" title="Void & Restore Stock"
                                className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                                onClick={async () => {
                                  if (!window.confirm(`Void invoice ${invoice.invoice_number}?\n\nThis will:\n• Restore inventory stock\n• Reset the PO to pending\n• Mark this invoice as voided`)) return;
                                  try {
                                    const r = await axios.post(`${API}/invoice/${invoice.id}/void`);
                                    toast.success(r.data.message);
                                    loadInvoices();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
                                }}
                                data-testid={`void-${invoice.invoice_number}`}>
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            {invoice.voided && (
                              <span className="text-xs text-amber-600 font-medium px-2">VOIDED</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </Card>
        </TabsContent>

        <TabsContent value="batches">
          {/* Batch invoices - for bulk printing */}
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-sm font-medium">
                Invoice Batches - Print invoices generated together
              </CardTitle>
            </CardHeader>
            <ScrollArea className="h-[450px]">
              {batches.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Layers className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg">No batches found</p>
                  <p className="text-sm mt-2">Use "Generate All" in PO Processing to create invoice batches</p>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase">Batch ID</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase">Invoices</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase">Invoice Numbers</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Total Value</TableHead>
                      <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.map((batch) => (
                      <TableRow key={batch._id}>
                        <TableCell className="font-mono text-sm">
                          {batch._id?.slice(0, 8)}...
                        </TableCell>
                        <TableCell className="font-mono font-medium">
                          <Badge variant="secondary">{batch.count} invoices</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                          {batch.invoices?.slice(0, 3).join(', ')}{batch.invoices?.length > 3 ? '...' : ''}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          Rs. {batch.total_value?.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleBatchDownload(batch._id)}
                              disabled={downloading}
                              title="Download Batch PDF"
                            >
                              <Download className="w-4 h-4 mr-1" />PDF
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => handleBatchPrint(batch._id)}
                              disabled={downloading}
                              title="Print All in Batch"
                            >
                              <Printer className="w-4 h-4 mr-1" />Print All
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cancel Invoice Dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel invoice <strong>{cancelTarget?.invoice_number}</strong>?
              This will restore {cancelTarget?.total_pcs || 0} items back to inventory. The invoice number will not be reused.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={cancelling} data-testid="cancel-dialog-no">
              No, Keep It
            </Button>
            <Button variant="destructive" onClick={handleCancelInvoice} disabled={cancelling} data-testid="cancel-dialog-yes">
              {cancelling ? "Cancelling..." : "Yes, Cancel Invoice"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function OBLIKInvoiceSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getSettings().then(r => setSettings(r.data)).catch(() => {}); }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await updateSettings(settings); toast.success("OBLIK invoice settings saved"); } catch { toast.error("Failed"); }
    setSaving(false);
  };

  if (!settings) return null;

  return (
    <Card data-testid="oblik-invoice-settings">
      <CardHeader className="py-3 px-4 border-b"><CardTitle className="text-sm">OBLIK Invoice Settings</CardTitle></CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><label className="text-xs font-medium text-slate-600">Invoice Prefix</label><Input value={settings.invoice_prefix || ""} onChange={e => setSettings({...settings, invoice_prefix: e.target.value})} className="mt-1" data-testid="oblik-prefix" /></div>
          <div><label className="text-xs font-medium text-slate-600">Financial Year</label><Input value={settings.financial_year || ""} onChange={e => setSettings({...settings, financial_year: e.target.value})} className="mt-1" /></div>
          <div><label className="text-xs font-medium text-slate-600">GST Rate (%)</label><Input type="number" value={settings.gst_rate || 18} onChange={e => setSettings({...settings, gst_rate: parseFloat(e.target.value)})} className="mt-1" /></div>
          <div><label className="text-xs font-medium text-slate-600">Last Invoice #</label><Input type="number" value={settings.last_invoice_number || 100} onChange={e => setSettings({...settings, last_invoice_number: parseInt(e.target.value)})} className="mt-1" /></div>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} className="mt-3" data-testid="oblik-save-settings"><Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save Settings"}</Button>
      </CardContent>
    </Card>
  );
}
